import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchMyStore } from '../store/storeSlice';
import Shell from '../dashboard/Shell';
import Modal from '../../components/ui/Modal';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Badge from '../../components/ui/Badge';
import { useToast } from '../../components/ui/Toast';
import { useConfirm } from '../../components/ui/ConfirmDialog';
import { getPlatformDomainName, getStoreSubdomain, getStoreUrl } from '../../config/platformConfig';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import {
  Globe,
  Plus,
  CheckCircle,
  RefreshCw,
  ExternalLink,
  Copy,
  Info,
  Search,
  Trash2,
  Clock,
  Edit3,
  Lock,
  CreditCard,
  ShieldCheck,
} from 'lucide-react';
import './CustomDomains.css';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '/api';

const STATUS_BADGE = {
  PENDING: { variant: 'warning', text: 'Pending verification' },
  VERIFIED: { variant: 'success', text: 'Verified' },
  ACTIVE: { variant: 'success', text: 'Active' },
  GRACE_PERIOD: { variant: 'warning', text: 'Grace period' },
  SUSPENDED: { variant: 'error', text: 'Suspended' },
  FAILED: { variant: 'error', text: 'Failed' },
};

const getStatusBadge = (status) => {
  const cfg = STATUS_BADGE[status] || STATUS_BADGE.PENDING;
  return <Badge variant={cfg.variant}>{cfg.text}</Badge>;
};

const formatMoney = (amount, currency = 'NGN') => {
  const value = Number(amount || 0);
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency,
    minimumFractionDigits: value % 1 === 0 ? 0 : 2,
  }).format(value);
};

// Normalize backend DNS instructions into a single shape:
// { records: [{type, host, value, label}], notes: string[] }.
// Handles Vercel (instructions[]) and the Caddy fallback (cname + txt).
// Legacy AWS shapes (validation[], traffic{}) are intentionally dropped —
// that provider path is retired.
const normalizeDnsInstructions = (raw) => {
  if (!raw) return null;
  const records = [];
  const notes = [];

  if (Array.isArray(raw.instructions) && raw.instructions.length) {
    raw.instructions.forEach((step) => {
      if (step.record) {
        records.push({
          type: step.record.type,
          host: step.record.host,
          value: step.record.value,
          label: step.description,
        });
      } else if (step.description) {
        notes.push(step.note ? `${step.description} — ${step.note}` : step.description);
      }
    });
  }

  if (raw.cname) {
    records.push({
      type: 'CNAME',
      host: raw.cname.host,
      value: raw.cname.value,
      label: 'Point your domain to our proxy',
    });
  }
  if (raw.txt) {
    records.push({
      type: 'TXT',
      host: raw.txt.host,
      value: raw.txt.value,
      label: 'Verify ownership',
    });
  }

  if (raw.estimatedTime) notes.push(`Estimated time: ${raw.estimatedTime}`);
  if (raw.note) notes.push(raw.note);

  return { records, notes };
};

const DnsRecord = ({ record, onCopy }) => (
  <div className="cd-dns-record">
    <div className="cd-dns-record-head">
      <span className="cd-dns-record-type">{record.type}</span>
      {record.label ? <span className="cd-dns-record-label">{record.label}</span> : null}
    </div>
    <div className="cd-dns-record-row">
      <span className="cd-dns-record-key">Host</span>
      <code className="cd-dns-record-value">{record.host}</code>
      <button
        type="button"
        className="cd-icon-btn"
        onClick={() => onCopy(record.host, 'Host')}
        aria-label="Copy host"
      >
        <Copy size={14} />
      </button>
    </div>
    <div className="cd-dns-record-row">
      <span className="cd-dns-record-key">Value</span>
      <code className="cd-dns-record-value">{record.value}</code>
      <button
        type="button"
        className="cd-icon-btn"
        onClick={() => onCopy(record.value, 'Value')}
        aria-label="Copy value"
      >
        <Copy size={14} />
      </button>
    </div>
  </div>
);

const CustomDomains = () => {
  const dispatch = useDispatch();
  const toast = useToast();
  const { confirm } = useConfirm();
  const { current: store, status: storeStatus } = useSelector((s) => s.store);
  const { hasAccess: hasCustomDomainAccess, loading: featureLoading } = useFeatureAccess('store.custom_domains');

  const [domains, setDomains] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [verifyingId, setVerifyingId] = useState(null);

  const [addOpen, setAddOpen] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  const [submittingAdd, setSubmittingAdd] = useState(false);

  const [dnsModalOpen, setDnsModalOpen] = useState(false);
  const [dnsInstructions, setDnsInstructions] = useState(null);

  const [slugOpen, setSlugOpen] = useState(false);
  const [slugDraft, setSlugDraft] = useState('');
  const [slugAvailable, setSlugAvailable] = useState(null);
  const [slugError, setSlugError] = useState('');
  const [checkingSlug, setCheckingSlug] = useState(false);
  const [savingSlug, setSavingSlug] = useState(false);
  const [buyOpen, setBuyOpen] = useState(false);
  const [buyDomain, setBuyDomain] = useState('');
  const [buyQuote, setBuyQuote] = useState(null);
  const [quoteError, setQuoteError] = useState('');
  const [quotingDomain, setQuotingDomain] = useState(false);
  const [startingCheckout, setStartingCheckout] = useState(false);
  const [verifyingDomainPayment, setVerifyingDomainPayment] = useState(false);

  const platformDomain = getPlatformDomainName();
  const currentSlug = store?.slug || '';
  const storeSubdomain = currentSlug ? getStoreSubdomain(currentSlug) : '';
  const storeUrl = currentSlug ? getStoreUrl(currentSlug) : '';

  const normalizeSlugInput = (value) => value.toLowerCase().replace(/[^a-z0-9-]/g, '');

  const validateSlug = (value) => {
    if (!value) return 'Enter a store URL';
    if (value.length < 3) return 'Store URL must be at least 3 characters';
    if (value.length > 50) return 'Store URL must be less than 50 characters';
    if (!/^[a-z0-9-]+$/.test(value)) return 'Use only lowercase letters, numbers, and hyphens';
    if (value.startsWith('-') || value.endsWith('-')) return 'Store URL cannot start or end with a hyphen';
    if (value.includes('--')) return 'Store URL cannot contain consecutive hyphens';
    return '';
  };

  const fetchDomains = useCallback(async () => {
    if (!hasCustomDomainAccess) {
      setDomains([]);
      setLoadingList(false);
      return;
    }

    setLoadingList(true);
    try {
      const res = await fetch(`${API_BASE_URL}/custom-domains/merchant`, {
        credentials: 'include',
      });
      const data = await res.json();
      if (data.success) {
        setDomains(Array.isArray(data.data) ? data.data : []);
      } else {
        toast.error(data.error || data.message || 'Failed to load domains');
      }
    } catch {
      toast.error('Failed to load domains');
    } finally {
      setLoadingList(false);
    }
  }, [hasCustomDomainAccess, toast]);

  useEffect(() => {
    if (!featureLoading) {
      fetchDomains();
    }
    dispatch(fetchMyStore());
  }, [fetchDomains, dispatch, featureLoading]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const reference = params.get('domainPayment');
    if (!reference || verifyingDomainPayment) return;

    const verifyPayment = async () => {
      setVerifyingDomainPayment(true);
      try {
        const res = await fetch(`${API_BASE_URL}/custom-domains/purchase/verify`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reference }),
        });
        const data = await res.json();
        if (!res.ok || data.success === false) {
          throw new Error(data.error || data.message || 'Could not verify domain payment');
        }
        toast.success(data.message || 'Domain payment confirmed');
        fetchDomains();
      } catch (error) {
        toast.error(error.message || 'Could not verify domain payment');
      } finally {
        params.delete('domainPayment');
        const nextSearch = params.toString();
        window.history.replaceState({}, '', `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}`);
        setVerifyingDomainPayment(false);
      }
    };

    verifyPayment();
  }, [fetchDomains, toast, verifyingDomainPayment]);

  const openSlugModal = () => {
    setSlugDraft(currentSlug);
    setSlugAvailable(null);
    setSlugError('');
    setSlugOpen(true);
  };

  const handleSlugDraftChange = (value) => {
    setSlugDraft(normalizeSlugInput(value));
    setSlugAvailable(null);
    setSlugError('');
  };

  const checkSlugAvailability = async () => {
    const cleaned = normalizeSlugInput(slugDraft.trim());
    setSlugDraft(cleaned);

    const localError = validateSlug(cleaned);
    if (localError) {
      setSlugError(localError);
      setSlugAvailable(false);
      return false;
    }

    if (cleaned === currentSlug) {
      setSlugError('This is already your current store URL');
      setSlugAvailable(false);
      return false;
    }

    setCheckingSlug(true);
    setSlugError('');
    try {
      const res = await fetch(
        `${API_BASE_URL}/stores/check-slug?slug=${encodeURIComponent(cleaned)}&excludeStoreId=${encodeURIComponent(store?.id || '')}`,
        { credentials: 'include' },
      );
      const data = await res.json();
      if (data.available) {
        setSlugAvailable(true);
        return true;
      }
      setSlugAvailable(false);
      setSlugError(data.error || 'This store URL is already taken');
      return false;
    } catch {
      setSlugAvailable(false);
      setSlugError('Could not verify URL availability. Please try again.');
      return false;
    } finally {
      setCheckingSlug(false);
    }
  };

  const handleSaveSlug = async () => {
    if (savingSlug) return;

    const ok = slugAvailable === true ? true : await checkSlugAvailability();
    if (!ok) return;

    setSavingSlug(true);
    try {
      const res = await fetch(`${API_BASE_URL}/stores/change-slug`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ newSlug: slugDraft }),
      });
      const data = await res.json();
      if (!res.ok || data.success === false) {
        throw new Error(data.error || data.message || 'Failed to update store URL');
      }
      toast.success(data.message || 'Store URL updated');
      setSlugOpen(false);
      dispatch(fetchMyStore());
    } catch (error) {
      toast.error(error.message || 'Failed to update store URL');
      setSlugError(error.message || 'Failed to update store URL');
    } finally {
      setSavingSlug(false);
    }
  };

  const copyToClipboard = useCallback(
    async (text, label = 'Copied') => {
      try {
        await navigator.clipboard.writeText(text);
        toast.success(`${label} copied`);
      } catch {
        toast.error("Couldn't copy");
      }
    },
    [toast],
  );

  const handleAddDomain = async (e) => {
    e.preventDefault();
    const trimmed = newDomain.trim();
    if (!trimmed) {
      toast.error('Enter a domain name');
      return;
    }
    if (!store?.id) {
      toast.error('Store is still loading — please wait a moment');
      return;
    }
    setSubmittingAdd(true);
    try {
      const res = await fetch(`${API_BASE_URL}/custom-domains`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain: trimmed, storeId: store.id }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success(data.message || 'Domain added — configure DNS to verify');
        if (data.data?.dnsInstructions) {
          setDnsInstructions(normalizeDnsInstructions(data.data.dnsInstructions));
          setDnsModalOpen(true);
        }
        setAddOpen(false);
        setNewDomain('');
        fetchDomains();
      } else {
        toast.error(data.error || data.message || 'Failed to add domain');
      }
    } catch {
      toast.error('Failed to add domain');
    } finally {
      setSubmittingAdd(false);
    }
  };

  const handleVerifyDomain = async (id) => {
    setVerifyingId(id);
    try {
      const res = await fetch(`${API_BASE_URL}/custom-domains/${id}/verify`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (data.success) {
        toast.success(data.message || 'Domain verified — SSL is provisioning');
        fetchDomains();
      } else {
        const fallbackDns =
          data?.data?.dnsInstructions ??
          (typeof data?.error === 'object' ? data.error.dnsInstructions : null);
        if (fallbackDns) {
          setDnsInstructions(normalizeDnsInstructions(fallbackDns));
          setDnsModalOpen(true);
        }
        const message =
          (typeof data.error === 'string' && data.error) ||
          data?.data?.message ||
          (typeof data.error === 'object' && data.error?.message) ||
          'Verification failed — has DNS propagated yet?';
        toast.error(message);
      }
    } catch {
      toast.error('Failed to verify domain');
    } finally {
      setVerifyingId(null);
    }
  };

  const handleDeleteDomain = async (domain) => {
    const ok = await confirm(
      `Delete ${domain.domain}? Visitors will stop reaching your store on this domain.`,
      {
        title: 'Delete domain',
        confirmText: 'Delete',
        cancelText: 'Keep',
        type: 'danger',
      },
    );
    if (!ok) return;
    try {
      const res = await fetch(`${API_BASE_URL}/custom-domains/${domain.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Domain deleted');
        fetchDomains();
      } else {
        toast.error(data.error || 'Failed to delete domain');
      }
    } catch {
      toast.error('Failed to delete domain');
    }
  };

  const openBuyModal = () => {
    if (!hasCustomDomainAccess) {
      toast.info('Domain purchase will be available on paid plans.');
      return;
    }
    setBuyQuote(null);
    setQuoteError('');
    setBuyOpen(true);
  };

  const handleQuoteDomain = async () => {
    const trimmed = buyDomain.trim();
    if (!trimmed) {
      setQuoteError('Enter a domain name');
      return null;
    }
    setQuotingDomain(true);
    setQuoteError('');
    try {
      const res = await fetch(`${API_BASE_URL}/custom-domains/purchase/quote`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain: trimmed, regPeriod: 1 }),
      });
      const data = await res.json();
      if (!res.ok || data.success === false) {
        throw new Error(data.error || data.message || 'Could not quote this domain');
      }
      setBuyQuote(data.data);
      return data.data;
    } catch (error) {
      setBuyQuote(null);
      setQuoteError(error.message || 'Could not quote this domain');
      return null;
    } finally {
      setQuotingDomain(false);
    }
  };

  const handleStartDomainCheckout = async () => {
    if (!store?.id || startingCheckout) return;
    const quote = buyQuote || await handleQuoteDomain();
    if (!quote) return;
    if (!quote.purchaseEnabled) {
      setQuoteError('Domain checkout is not enabled yet.');
      return;
    }

    setStartingCheckout(true);
    setQuoteError('');
    try {
      const res = await fetch(`${API_BASE_URL}/custom-domains/purchase`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          domain: quote.domain,
          storeId: store.id,
          regPeriod: quote.regPeriod || 1,
          contactInfo: {
            company: store.name,
            email: undefined,
          },
        }),
      });
      const data = await res.json();
      if (!res.ok || data.success === false) {
        throw new Error(data.error || data.message || 'Could not start checkout');
      }
      window.location.href = data.data.authorizationUrl;
    } catch (error) {
      setQuoteError(error.message || 'Could not start checkout');
    } finally {
      setStartingCheckout(false);
    }
  };

  const hasDomains = domains.length > 0;
  const storeName = store?.name || 'your store';
  const domainActionsLocked = !featureLoading && !hasCustomDomainAccess;

  return (
    <Shell>
      <div className="cd-page">
        <header className="cd-header">
          <div className="cd-header-text">
            <h1 className="cd-title">Domain</h1>
            <p className="cd-subtitle">
              Manage your Revstar URL, connect an existing domain, or buy a new domain for {storeName}.
            </p>
          </div>
          <div className="cd-header-actions">
            <Button
              className={`cd-header-btn ${domainActionsLocked ? 'cd-header-btn-locked' : ''}`}
              variant="secondary"
              onClick={() => hasCustomDomainAccess ? setAddOpen(true) : toast.info('Custom domains are available on paid plans.')}
              disabled={featureLoading}
              aria-label={domainActionsLocked ? 'Connect domain, paid plan required' : 'Connect domain'}
            >
              {hasCustomDomainAccess ? <Globe size={16} /> : <Lock size={16} />}
              <span>Connect domain</span>
            </Button>
            <Button
              className={`cd-header-btn ${domainActionsLocked ? 'cd-header-btn-locked' : ''}`}
              onClick={openBuyModal}
              disabled={featureLoading}
              aria-label={domainActionsLocked ? 'Buy domain, paid plan required' : 'Buy domain'}
            >
              {hasCustomDomainAccess ? <Plus size={16} /> : <Lock size={16} />}
              <span>Buy domain</span>
            </Button>
          </div>
        </header>

        <section className="cd-card cd-revstar-url">
          <div className="cd-revstar-url-main">
            <div className="cd-revstar-url-label">Revstar URL</div>
            <div className="cd-revstar-url-value">
              {storeSubdomain || 'Store URL unavailable'}
            </div>
          </div>
          <div className="cd-revstar-url-actions">
            {storeUrl ? (
              <a
                className="cd-pill-btn"
                href={storeUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLink size={14} />
                <span>Visit</span>
              </a>
            ) : null}
            <button
              type="button"
              className="cd-pill-btn"
              onClick={openSlugModal}
              disabled={!currentSlug}
            >
              <Edit3 size={14} />
              <span>Edit URL</span>
            </button>
          </div>
        </section>

        {featureLoading ? (
          <section className="cd-empty">
            <RefreshCw size={20} className="cd-spin" />
            <p>Loading domain access…</p>
          </section>
        ) : !hasCustomDomainAccess ? (
          <section className="cd-card cd-locked-card">
            <div className="cd-locked-icon">
              <Lock size={20} />
            </div>
            <div className="cd-locked-copy">
              <h2>Custom domains</h2>
              <p>
                Your Revstar URL is available on every plan. Upgrade when you want to connect a domain you already own or buy one through Revstar.
              </p>
            </div>
          </section>
        ) : (
          <>
          {/* Existing domains */}
          <section className="cd-list">
            <div className="cd-list-head">
              <h2 className="cd-section-title">Your domains</h2>
              {hasDomains ? <span className="cd-count">{domains.length}</span> : null}
            </div>

            {loadingList ? (
              <div className="cd-empty">
                <RefreshCw size={20} className="cd-spin" />
                <p>Loading domains…</p>
              </div>
            ) : !hasDomains ? (
              <div className="cd-empty">
                <Globe size={28} />
                <h3>No domains yet</h3>
                <p>Connect a domain you already own when you are ready to use it for your store.</p>
                <Button onClick={() => setAddOpen(true)}>
                  <Plus size={14} />
                  <span>Connect a domain</span>
                </Button>
              </div>
            ) : (
              <div className="cd-grid">
                {domains.map((d) => {
                  const dns = d.dnsInstructions ? normalizeDnsInstructions(d.dnsInstructions) : null;
                  const visitable = d.status === 'VERIFIED' || d.status === 'ACTIVE';
                  return (
                    <article key={d.id} className="cd-card cd-domain">
                      <header className="cd-domain-head">
                        <div className="cd-domain-name">
                          <Globe size={16} />
                          <strong>{d.domain}</strong>
                        </div>
                        {getStatusBadge(d.status)}
                      </header>

                      <dl className="cd-domain-meta">
                        <div className="cd-domain-row">
                          <dt>Store</dt>
                          <dd>{d.store?.name || store?.name || '—'}</dd>
                        </div>
                        <div className="cd-domain-row">
                          <dt>Subdomain</dt>
                          <dd className="cd-mono">
                            {d.store?.slug ? `${d.store.slug}.${platformDomain}` : '—'}
                          </dd>
                        </div>
                        <div className="cd-domain-row">
                          <dt>Added</dt>
                          <dd>{new Date(d.createdAt).toLocaleDateString()}</dd>
                        </div>
                        {d.graceEndsAt ? (
                          <div className="cd-domain-row cd-domain-row-warn">
                            <dt>
                              <Clock size={12} /> Grace ends
                            </dt>
                            <dd>{new Date(d.graceEndsAt).toLocaleDateString()}</dd>
                          </div>
                        ) : null}
                      </dl>

                      {d.status === 'PENDING' && dns?.records?.length ? (
                        <button
                          type="button"
                          className="cd-pending-banner"
                          onClick={() => {
                            setDnsInstructions(dns);
                            setDnsModalOpen(true);
                          }}
                        >
                          <Info size={14} />
                          <span>DNS configuration required — tap to view records</span>
                        </button>
                      ) : null}

                      <footer className="cd-domain-actions">
                        <button
                          type="button"
                          className="cd-pill-btn"
                          onClick={() => handleVerifyDomain(d.id)}
                          disabled={verifyingId === d.id}
                        >
                          <RefreshCw
                            size={14}
                            className={verifyingId === d.id ? 'cd-spin' : ''}
                          />
                          <span>{verifyingId === d.id ? 'Verifying…' : 'Verify'}</span>
                        </button>
                        {visitable ? (
                          <a
                            className="cd-pill-btn"
                            href={`https://${d.domain}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <ExternalLink size={14} />
                            <span>Visit</span>
                          </a>
                        ) : null}
                        <button
                          type="button"
                          className="cd-pill-btn cd-pill-btn-danger"
                          onClick={() => handleDeleteDomain(d)}
                        >
                          <Trash2 size={14} />
                          <span>Remove</span>
                        </button>
                      </footer>
                    </article>
                  );
                })}
              </div>
            )}
          </section>
          </>
        )}

          {/* Revstar URL modal */}
          <Modal isOpen={slugOpen} onClose={() => setSlugOpen(false)} title="Edit Revstar URL">
            <div className="cd-form">
              <div className="cd-form-row">
                <label htmlFor="cd-slug" className="cd-label">Store URL</label>
                <div className="cd-slug-input-row">
                  <Input
                    id="cd-slug"
                    value={slugDraft}
                    onChange={(e) => handleSlugDraftChange(e.target.value)}
                    onBlur={checkSlugAvailability}
                    placeholder="your-store"
                    autoFocus
                  />
                  <span className="cd-slug-suffix">.{platformDomain}</span>
                </div>
                <small className="cd-help">
                  Changing this updates your Revstar storefront URL. Old URLs are reserved temporarily and redirect where supported.
                </small>
                {slugError ? (
                  <div className="cd-inline-error">{slugError}</div>
                ) : slugAvailable ? (
                  <div className="cd-inline-success">This URL is available.</div>
                ) : null}
              </div>

              <div className="cd-url-preview">
                <span>Preview</span>
                <strong>{slugDraft ? getStoreSubdomain(slugDraft) : storeSubdomain}</strong>
              </div>

              <div className="cd-form-actions">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setSlugOpen(false)}
                  disabled={savingSlug}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={checkSlugAvailability}
                  disabled={checkingSlug || savingSlug || !slugDraft || slugDraft === currentSlug}
                >
                  {checkingSlug ? <RefreshCw size={14} className="cd-spin" /> : <Search size={14} />}
                  <span>{checkingSlug ? 'Checking…' : 'Check availability'}</span>
                </Button>
                <Button
                  type="button"
                  onClick={handleSaveSlug}
                  disabled={savingSlug || checkingSlug || !slugDraft || slugDraft === currentSlug}
                >
                  {savingSlug ? <RefreshCw size={14} className="cd-spin" /> : <CheckCircle size={14} />}
                  <span>{savingSlug ? 'Saving…' : 'Save URL'}</span>
                </Button>
              </div>
            </div>
          </Modal>

          {/* Add modal */}
          <Modal isOpen={addOpen} onClose={() => setAddOpen(false)} title="Add a custom domain">
            <form className="cd-form" onSubmit={handleAddDomain}>
              <div className="cd-form-row">
                <label htmlFor="cd-store" className="cd-label">Store</label>
                <Input
                  id="cd-store"
                  value={
                    storeStatus === 'loading'
                      ? 'Loading…'
                      : store?.name || 'No store found'
                  }
                  disabled
                />
                <small className="cd-help">Domain will be linked to this store.</small>
              </div>
              <div className="cd-form-row">
                <label htmlFor="cd-domain" className="cd-label">Domain</label>
                <Input
                  id="cd-domain"
                  value={newDomain}
                  onChange={(e) => setNewDomain(e.target.value)}
                  placeholder="example.com"
                  required
                  autoFocus
                />
                <small className="cd-help">No <code>www.</code> — just the root domain.</small>
              </div>
              <div className="cd-form-actions">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setAddOpen(false)}
                  disabled={submittingAdd}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={submittingAdd || !newDomain.trim() || !store?.id}>
                  {submittingAdd ? <RefreshCw size={14} className="cd-spin" /> : <Plus size={14} />}
                  <span>{submittingAdd ? 'Adding…' : 'Add domain'}</span>
                </Button>
              </div>
            </form>
          </Modal>

          {/* Buy domain modal */}
          <Modal isOpen={buyOpen} onClose={() => setBuyOpen(false)} title="Buy domain">
            <div className="cd-form">
              <div className="cd-form-row">
                <label htmlFor="cd-buy-domain" className="cd-label">Domain name</label>
                <div className="cd-buy-input-row">
                  <Input
                    id="cd-buy-domain"
                    value={buyDomain}
                    onChange={(e) => {
                      setBuyDomain(e.target.value);
                      setBuyQuote(null);
                      setQuoteError('');
                    }}
                    placeholder="yourstore.com"
                    autoFocus
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handleQuoteDomain}
                    disabled={quotingDomain || !buyDomain.trim()}
                  >
                    {quotingDomain ? <RefreshCw size={14} className="cd-spin" /> : <Search size={14} />}
                    <span>{quotingDomain ? 'Checking…' : 'Check'}</span>
                  </Button>
                </div>
                <small className="cd-help">Enter the root domain without <code>www.</code>.</small>
              </div>

              {quoteError ? <div className="cd-inline-error">{quoteError}</div> : null}

              {buyQuote ? (
                <div className="cd-domain-quote">
                  <div className="cd-domain-quote-head">
                    <div>
                      <span>Available quote</span>
                      <strong>{buyQuote.domain}</strong>
                    </div>
                    <ShieldCheck size={18} />
                  </div>
                  <div className="cd-domain-quote-lines">
                    <div>
                      <span>Registration</span>
                      <strong>{formatMoney(buyQuote.baseAmount, buyQuote.currency)}</strong>
                    </div>
                    <div>
                      <span>{buyQuote.vat?.name || 'VAT'} {buyQuote.vat?.rate ? `(${buyQuote.vat.rate}%)` : ''}</span>
                      <strong>{formatMoney(buyQuote.vat?.amount, buyQuote.currency)}</strong>
                    </div>
                    <div className="cd-domain-quote-total">
                      <span>Total</span>
                      <strong>{formatMoney(buyQuote.totalAmount, buyQuote.currency)}</strong>
                    </div>
                  </div>
                  {!buyQuote.purchaseEnabled ? (
                    <p className="cd-domain-quote-note">
                      Checkout is not enabled yet. Revstar can show pricing now, but payment will stay locked until domain resale is turned on.
                    </p>
                  ) : null}
                  {!buyQuote.registrationEnabled ? (
                    <p className="cd-domain-quote-note">
                      Registration is set to manual provisioning after payment until Go54 live registration is enabled.
                    </p>
                  ) : null}
                </div>
              ) : null}

              <div className="cd-form-actions">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setBuyOpen(false)}
                  disabled={startingCheckout}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleStartDomainCheckout}
                  disabled={startingCheckout || quotingDomain || !buyDomain.trim() || (buyQuote && !buyQuote.purchaseEnabled)}
                >
                  {startingCheckout ? <RefreshCw size={14} className="cd-spin" /> : <CreditCard size={14} />}
                  <span>{startingCheckout ? 'Starting…' : 'Pay with Paystack'}</span>
                </Button>
              </div>
            </div>
          </Modal>

          {/* DNS instructions modal */}
          <Modal
            isOpen={dnsModalOpen}
            onClose={() => setDnsModalOpen(false)}
            title="DNS configuration"
            size="lg"
          >
            <div className="cd-dns-modal">
              <p className="cd-dns-intro">
                Add these records at your DNS provider (Namecheap, GoDaddy, Cloudflare, …). Propagation usually takes a few minutes.
              </p>

              {dnsInstructions?.records?.length ? (
                <div className="cd-dns-records">
                  {dnsInstructions.records.map((r, i) => (
                    <DnsRecord key={`${r.type}-${i}`} record={r} onCopy={copyToClipboard} />
                  ))}
                </div>
              ) : (
                <p className="cd-dns-empty">No DNS records found in the response.</p>
              )}

              {dnsInstructions?.notes?.length ? (
                <div className="cd-dns-notes">
                  {dnsInstructions.notes.map((n, i) => (
                    <div key={i} className="cd-dns-note">
                      <Info size={14} />
                      <span>{n}</span>
                    </div>
                  ))}
                </div>
              ) : null}

              <div className="cd-form-actions">
                <Button variant="secondary" onClick={() => setDnsModalOpen(false)}>Close</Button>
              </div>
            </div>
          </Modal>

        </div>
    </Shell>
  );
};

export default CustomDomains;
